import { SearchTriggerComponent } from '@core/components/universal-search/search-trigger.component';
import {
  Component,
  ElementRef,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  selectCurrentProject,
  selectUnderProjectRoute,
} from '@core/state/entities/project/project.selectors';
import {
  selectCurrentStage,
  selectUnderStageRoute,
} from '@core/state/entities/stage/stage.selectors';
import {
  selectAuthenticatedUser,
  selectAuthIsIdle,
  selectIsAuthenticated,
  selectIsImpersonating,
  selectProfilePictureUrl,
} from '@core/auth/auth.selectors';
import { AppConfigService } from '@core/services/config/app-config.service';
import {
  selectFavouriteProjects,
  selectFirstFiveRecentProjects,
} from '@core/state/entities/project-usage/project-usage.selectors';
import { FeatureToggleService } from '@core/services/feature-toggle/feature-toggle.service';
import {
  getShowChangeDetectionVisualisationState,
  getShowDebuggerState,
  getShowLoggingState,
} from '@core/state/ui/application/application-ui.selectors';
import { authApiActions } from '@core/auth/auth.actions';
import { v4 as uuid } from 'uuid';
import { GuidDecodeComponent } from '@app/admin/guid-decode/guid-decode.component';
import { MatDialog } from '@angular/material/dialog';
import { ReviewPreferencesDialogComponent } from '@app/stage/stage-review/review-preferences-dialog/review-preferences-dialog.component';
import {
  selectStageReviewRedesign,
  selectThemeToggle,
} from '@app/generated/feature-flags.selectors';
import { VersionInfoDialogComponent } from '@shared/dialogs/version-info-dialog/version-info-dialog.component';
import { EnvironmentWarningDialogComponent } from '../components/environment-warning-dialog/environment-warning-dialog.component';
import { EnvironmentChipComponent } from '../components/environment-chip/environment-chip.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { HoverDirective } from '@shared/hover/hover.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import {
  selectApplicationCapability,
  selectCanAdministerApplication,
  selectShowAdminUiOptions,
} from '@core/state/ui/admin/admin-ui.selectors';

import { adminActions } from '@core/actions/admin.actions';
import { ProjectHttpService } from '@core/services/api-client.generated';
import { ErrorRecoveryDialogComponent } from '../components/error-recovery-dialog/error-recovery-dialog.component';
import { UserGuideUrlPipe } from '@shared/pipes/user-guide-url/user-guide-url.pipe';
import { ProjectNavigationService } from '@core/services/layout/project-navigation.service';
import { LayoutService } from '@core/services/layout/layout.service';
import { ThemeMode, ThemeService } from '@core/services/theme/theme.service';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property,@typescript-eslint/naming-convention
  host: { '[class.change-detecting]': 'changeDetection' },
  // The template reads only signals, inputs and constant fields, so OnPush
  // re-renders on exactly the changes it displays. `blink()` still flashes on
  // every check this view actually performs, which is what it visualises.
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SearchTriggerComponent,
    MatToolbarModule,
    MatButtonModule,
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    HoverDirective,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    EnvironmentChipComponent,
    UserGuideUrlPipe,
  ],
})
export class NavComponent {
  private _store: Store = inject(Store);
  public route = inject(ActivatedRoute);
  _router = inject(Router);
  _appConfigService = inject(AppConfigService);
  _featureToggleService = inject(FeatureToggleService);
  _el = inject(ElementRef);
  _dialog = inject(MatDialog);
  /** §5 preferences only govern the redesigned review page. */
  readonly stageReviewRedesign = this._store.selectSignal(
    selectStageReviewRedesign
  );
  readonly themeToggle = this._store.selectSignal(selectThemeToggle);
  readonly appIsUnstable = input(false);
  readonly $underProjectRoute = this._store.selectSignal(
    selectUnderProjectRoute
  );
  readonly $underStageRoute = this._store.selectSignal(selectUnderStageRoute);
  readonly authenticated = this._store.selectSignal(selectIsAuthenticated);
  readonly authIsIdle = this._store.selectSignal(selectAuthIsIdle);
  readonly isImpersonatingUser = this._store.selectSignal(
    selectIsImpersonating
  );
  readonly authenticatedUser = this._store.selectSignal(
    selectAuthenticatedUser
  );
  // M2a: the Admin menu and its entries present the backend capabilities where PM authority
  // decides, and the claim-group check elsewhere (unchanged). The API enforces every request.
  readonly isAdmin = this._store.selectSignal(selectCanAdministerApplication);
  readonly canListUsers = this._store.selectSignal(
    selectApplicationCapability('listUsers')
  );
  readonly canManageFeatureFlags = this._store.selectSignal(
    selectApplicationCapability('manageRuntimeFeatureFlags')
  );
  readonly canBatchAdminProjects = this._store.selectSignal(
    selectApplicationCapability('batchAdminProjects')
  );
  readonly profilePictureUrl = this._store.selectSignal(
    selectProfilePictureUrl
  );
  readonly favouriteProjects = this._store.selectSignal(
    selectFavouriteProjects
  );
  readonly recentProjects = this._store.selectSignal(
    selectFirstFiveRecentProjects
  );
  // The same selectors FeatureToggleService exposes as observables; the writes
  // still go through the service below.
  readonly showDebuggers = this._store.selectSignal(getShowDebuggerState);
  readonly showLogging = this._store.selectSignal(getShowLoggingState);
  readonly showChangeDetection = this._store.selectSignal(
    getShowChangeDetectionVisualisationState
  );
  changeDetection = false;
  randomId: string = this._appConfigService.config.randomId;
  showingAdminUiOptions = this._store.selectSignal(selectShowAdminUiOptions);
  currentProject = this._store.selectSignal(selectCurrentProject);
  currentStage = this._store.selectSignal(selectCurrentStage);
  _projectService = inject(ProjectHttpService);
  private readonly _layoutService = inject(LayoutService);
  readonly projectNavigation = inject(ProjectNavigationService);

  readonly compactNavigation = this._layoutService.compactApplicationShell;
  readonly themeService = inject(ThemeService);
  readonly themeMode = this.themeService.mode;
  readonly isDark = this.themeService.isDark;
  readonly themeModeLabel = this.themeService.modeLabel;

  setThemeMode(mode: ThemeMode): void {
    this.themeService.setMode(mode);
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  toggleProjectNavigation(): void {
    this.projectNavigation.toggle();
  }

  showErrorRecoveryDialog() {
    ErrorRecoveryDialogComponent.open(this._dialog);
  }
  async updateAnnotationAnswerTally() {
    const currentProject = this.currentProject();
    if (currentProject) {
      await firstValueFrom(
        this._projectService.updateAnnotationAnswerTally(currentProject.id)
      );
    }
  }
  blink() {
    // Under the zoneless bootstrap (wave 3) this setTimeout stops scheduling
    // change detection; until then zone.js still ticks on it. Harmless either
    // way because the blink components are OnPush.
    this._el.nativeElement.classList.add('highlight');
    setTimeout(() => {
      this._el.nativeElement.classList.remove('highlight');
    }, 1500);
  }

  /**
   * §1.1/§5: the user menu is the canonical way in to Review preferences.
   * Gated on the redesign flag because the preferences only govern the
   * redesigned review page; offering them while it is off would be a setting
   * with nothing to change.
   */
  openReviewPreferences(): void {
    ReviewPreferencesDialogComponent.open(this._dialog);
  }

  signOut() {
    this._store.dispatch(authApiActions.signOut({ onlyLocal: false }));
  }

  setChangeDetectionVisualisation(showChangeDetection) {
    this._featureToggleService.showChangeDetection(showChangeDetection);
  }

  setDebuggers(showDebuggers) {
    this._featureToggleService.showDebuggers(showDebuggers);
  }

  setLogging(showLogging) {
    this._featureToggleService.showLogging(showLogging);
  }

  signIn() {
    this._store.dispatch(authApiActions.startSignIn({ redirectUrl: null }));
  }

  onThrowError() {
    throw Error('Mock Error!');
  }

  openGuidDecoder() {
    this._dialog.open(GuidDecodeComponent, {
      width: '500px',
    });
  }

  openVersionInfo() {
    this._dialog.open(VersionInfoDialogComponent, {
      width: '450px',
      autoFocus: false,
    });
  }

  openEnvironmentWarning() {
    this._dialog.open(EnvironmentWarningDialogComponent, {
      width: '450px',
      autoFocus: true,
      ariaDescribedBy: 'environment-warning-dialog-message',
    });
  }

  updateAllProjectInclusion() {
    this._store.dispatch(
      projectDetailActions.updateAllProjectStudyInclusionInfo({
        replaceExisting: true,
        correlationId: uuid(),
      })
    );
  }

  toggleAdminUiOptions() {
    this._store.dispatch(adminActions.toggleAdminUiOptions());
  }
}
